/**
 * Cross-browser fullscreen helpers (web only).
 *
 * iOS Safari / WebKit does NOT implement the standard
 * `Element.requestFullscreen` — calling it throws
 * «e.requestFullscreen is not a function» (Sentry REACT-NATIVE-J, the
 * highest-volume prod error, 23× on iOS Yandex Browser in a practice room).
 * iPhone only exposes native fullscreen on a `<video>` element via
 * `webkitEnterFullscreen()`; iPad/desktop WebKit expose
 * `webkitRequestFullscreen` on elements. These helpers feature-detect every
 * variant and degrade to a no-op instead of crashing the room UI.
 *
 * DOM is only touched inside `typeof document` guards, so the module stays
 * safe to bundle for native (INVARIANT I-2) — the bodies never run there.
 */
type FsElement = HTMLElement & {
    webkitRequestFullscreen?: () => void | Promise<void>;
    webkitEnterFullscreen?: () => void; // <video> on iPhone
    webkitExitFullscreen?: () => void;  // <video> on iPhone — element-level, not document
    webkitDisplayingFullscreen?: boolean; // <video> on iPhone — the only "is it open" signal
    msRequestFullscreen?: () => void | Promise<void>;
};
type FsDocument = Document & {
    webkitExitFullscreen?: () => void | Promise<void>;
    msExitFullscreen?: () => void | Promise<void>;
    webkitFullscreenElement?: Element | null;
};

const swallow = (p: void | Promise<void>) => {
    if (p && typeof (p as Promise<void>).catch === 'function') (p as Promise<void>).catch(() => {});
};

/**
 * True if any fullscreen element is currently active (standard or webkit).
 *
 * CORE-852 — pass the `<video>` when you have one. On iPhone `webkitEnterFullscreen()`
 * opens the native player WITHOUT setting `document.fullscreenElement` or
 * `webkitFullscreenElement`: the only signal is `video.webkitDisplayingFullscreen`.
 * Without this argument the caller reads "not fullscreen" while the player is open,
 * so a toggle button re-enters instead of leaving — the mobile-web half of the
 * «полноэкранный режим корректно выходит обратно» criterion.
 */
export function isFullscreenActive(el?: HTMLElement | null): boolean {
    if (typeof document === 'undefined') return false;
    const d = document as FsDocument;
    if (document.fullscreenElement || d.webkitFullscreenElement) return true;
    return !!(el && (el as FsElement).webkitDisplayingFullscreen);
}

/**
 * True if THIS element is the one in fullscreen — unlike `isFullscreenActive`,
 * which also answers true when some other element (the page wrapper, a
 * different player) holds it. CORE-852: the practice `<video>` shows the
 * browser's native controls only when it is itself the fullscreen element.
 */
export function isElementFullscreen(el: HTMLElement | null | undefined): boolean {
    if (!el || typeof document === 'undefined') return false;
    const d = document as FsDocument;
    if (document.fullscreenElement === el || d.webkitFullscreenElement === el) return true;
    return !!(el as FsElement).webkitDisplayingFullscreen;
}

/** Best-effort enter fullscreen on `el`, tolerating WebKit/iOS/MS variants. */
export function enterFullscreen(el: HTMLElement | null | undefined): void {
    if (!el || typeof document === 'undefined') return;
    const e = el as FsElement;
    try {
        if (typeof e.requestFullscreen === 'function') { swallow(e.requestFullscreen()); return; }
        if (typeof e.webkitRequestFullscreen === 'function') { e.webkitRequestFullscreen(); return; }
        if (typeof e.msRequestFullscreen === 'function') { e.msRequestFullscreen(); return; }
        // iPhone: only native <video> fullscreen exists; container FS is unsupported.
        if (typeof e.webkitEnterFullscreen === 'function') { e.webkitEnterFullscreen(); return; }
    } catch {
        // A fullscreen toggle must never crash the surrounding UI.
    }
}

/**
 * Best-effort exit fullscreen, tolerating WebKit/MS variants.
 *
 * CORE-852 — pass the `<video>` when you have one. iPhone's native player is left
 * through `video.webkitExitFullscreen()`, an ELEMENT method; the document-level
 * exits this function tried first simply do not exist there, so without the
 * element the call was a silent no-op and the user could only escape via the
 * player's own «Done».
 */
export function exitFullscreen(el?: HTMLElement | null): void {
    if (typeof document === 'undefined') return;
    const d = document as FsDocument;
    try {
        if (typeof document.exitFullscreen === 'function') { swallow(document.exitFullscreen()); return; }
        if (typeof d.webkitExitFullscreen === 'function') { d.webkitExitFullscreen(); return; }
        if (typeof d.msExitFullscreen === 'function') { d.msExitFullscreen(); return; }
        const e = el as FsElement | null | undefined;
        if (e && typeof e.webkitExitFullscreen === 'function') { e.webkitExitFullscreen(); return; }
    } catch {
        // no-op
    }
}
